import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { differenceInDays } from "date-fns";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { updateEntityFrequency } from "@app/store/frequency/actions";
import { updateMetadata } from "@app/store/iam/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { parseApiDate } from "@app/util/parsers";

import useFrequencyContext from "../FrequencyPanel/util/useFrequencyContext";
import FrequencyEntity from "../type/FrequencyEntity";

import ActivityTypes from "./ActivityTypes";
import Footer from "./Footer";
import styles from "./FrequencyPreview.module.scss";
import Interval from "./Interval";
import messages from "./messages";

interface FrequencyPreviewProps {
  cadenceInterval?: Cadenced["cadenceInterval"];
  crmActivityTypeIds?: Cadenced["crmActivityTypeId"];
  entity?: FrequencyEntity;
  header?: ReactNode;
  isGroup?: boolean;
  lastCompletedActivity?: Activity;
  loading?: boolean;
  onCancel?: () => void;
  onSetFrequency?: (data: Partial<Cadenced>) => void;
  onUpdateMetadata: typeof updateMetadata.request;
  readonly?: boolean;
  saveButtonText?: string;
  showActivityTypes?: boolean;
  showSaveButtonsOnlyAfterChanges?: boolean;
  updateEntityFrequency: typeof updateEntityFrequency.request;
}

const FrequencyPreview = ({
  cadenceInterval,
  crmActivityTypeIds,
  entity,
  header,
  isGroup,
  lastCompletedActivity,
  loading,
  onCancel,
  onSetFrequency,
  onUpdateMetadata,
  readonly,
  saveButtonText,
  showActivityTypes = true,
  showSaveButtonsOnlyAfterChanges,
  updateEntityFrequency,
}: FrequencyPreviewProps) => {
  const intl = useIntl();
  const { onUpdate } = useFrequencyContext();
  const analytics = useAnalytics();

  const [currentCadenceInterval, setCurrentCadenceInterval] =
    useState<Cadenced["cadenceInterval"]>();
  const [selectedActivityTypeIds, setSelectedActivityTypeIds] = useState<ActivityType["id"][]>(
    crmActivityTypeIds ?? entity?.crmActivityTypeId ?? []
  );
  const [allActivityTypes, setAllActivityTypes] = useState<boolean>(
    (crmActivityTypeIds ?? entity?.crmActivityTypeId ?? []).length === 0
  );

  const [hasChanges, setChanges, unsetChanges] = useBoolean();

  useEffect(() => {
    setCurrentCadenceInterval(cadenceInterval ?? entity?.cadenceInterval ?? 0);
  }, [cadenceInterval, entity]);

  useEffect(() => {
    setAllActivityTypes((crmActivityTypeIds ?? entity?.crmActivityTypeId ?? []).length === 0);
  }, [crmActivityTypeIds, entity]);

  const handleSetCadence = useCallback(
    (cadence: Cadenced["cadenceInterval"]) => {
      setCurrentCadenceInterval(cadence);
      setChanges();
      analytics.clicked(["Change Interval"], { value: cadence });
    },
    [analytics, setCurrentCadenceInterval, setChanges]
  );

  const handleSetSelectedActivityTypeIds = useCallback(
    (types: ActivityType["id"][]) => {
      setSelectedActivityTypeIds(types);
      setChanges();
      analytics.clicked(["Toggle Accepted Activity Types"], { types });
    },
    [analytics, setSelectedActivityTypeIds, setChanges]
  );

  const handleSetAllActivityTypes = useCallback(
    (allActivityTypes: boolean) => {
      setAllActivityTypes(allActivityTypes);
      setChanges();
    },
    [setAllActivityTypes, setChanges]
  );

  const handleApply = useCallback(() => {
    if (entity) {
      updateEntityFrequency({
        callback: () => {
          onUpdate?.();
          unsetChanges();
        },
        entity: {
          ...entity,
          cadenceInterval: currentCadenceInterval,
          crmActivityTypeId: allActivityTypes ? [] : selectedActivityTypeIds,
        },
      });
    }
    onUpdateMetadata({ frequencyCustomInterval: currentCadenceInterval });
    onSetFrequency?.({
      cadenceInterval: currentCadenceInterval,
      crmActivityTypeId: allActivityTypes ? [] : selectedActivityTypeIds,
    });
    analytics.clicked(["Set Frequency"]);
  }, [
    allActivityTypes,
    analytics,
    currentCadenceInterval,
    entity,
    onSetFrequency,
    onUpdate,
    onUpdateMetadata,
    selectedActivityTypeIds,
    unsetChanges,
    updateEntityFrequency,
  ]);

  const handleDelete = useCallback(() => {
    if (entity) {
      updateEntityFrequency({
        callback: onUpdate,
        entity: {
          ...entity,
          cadenceInterval: null,
          crmActivityTypeId: null,
        },
      });
    }
    onSetFrequency?.({
      cadenceInterval: null,
      crmActivityTypeId: null,
    });
    analytics.clicked(["Remove"]);
  }, [analytics, entity, onSetFrequency, onUpdate, updateEntityFrequency]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setCurrentCadenceInterval(entity?.cadenceInterval);
    setSelectedActivityTypeIds(entity?.crmActivityTypeId ?? []);
    setAllActivityTypes((entity?.crmActivityTypeId ?? []).length === 0);
    unsetChanges();
    analytics.clicked(["Cancel"]);
  }, [
    analytics,
    entity,
    onCancel,
    setCurrentCadenceInterval,
    setSelectedActivityTypeIds,
    setAllActivityTypes,
    unsetChanges,
  ]);

  const lastActivityDaysAgo = useMemo(
    () =>
      lastCompletedActivity?.completedAt
        ? differenceInDays(new Date(), parseApiDate(lastCompletedActivity.completedAt))
        : 0,
    [lastCompletedActivity?.completedAt]
  );

  return (
    <div className={styles.container}>
      {header}
      <Interval cadence={currentCadenceInterval} onChange={handleSetCadence} readonly={readonly} />
      {!isGroup && lastCompletedActivity?.completedAt && (
        <Row align="middle" className={styles.line} justify="space-between">
          <Col>{intl.formatMessage(messages.lastActivityLogged)}</Col>
          <Col className={styles.staticValue}>
            {lastActivityDaysAgo > 0
              ? intl.formatMessage(messages.lastActivityLoggedValue, {
                  count: lastActivityDaysAgo,
                })
              : intl.formatMessage(messages.today)}
          </Col>
        </Row>
      )}
      {showActivityTypes && (
        <ActivityTypes
          allActivityTypes={allActivityTypes}
          onSetActivityTypes={handleSetSelectedActivityTypeIds}
          onSetAllActivityTypes={handleSetAllActivityTypes}
          readonly={readonly}
          selectedActivityTypeIds={selectedActivityTypeIds}
        />
      )}
      <Footer
        applyDisabled={
          !currentCadenceInterval || (!allActivityTypes && selectedActivityTypeIds.length === 0)
        }
        entity={entity}
        hasChanges={hasChanges}
        isGroup={isGroup}
        loading={loading}
        onApply={handleApply}
        onCancel={handleCancel}
        onDelete={handleDelete}
        readonly={readonly}
        saveButtonText={saveButtonText}
        showSaveButtonsOnlyAfterChanges={showSaveButtonsOnlyAfterChanges}
      />
    </div>
  );
};

const mapDispatchToProps = {
  onUpdateMetadata: updateMetadata.request,
  updateEntityFrequency: updateEntityFrequency.request,
};

export default connect(null, mapDispatchToProps)(FrequencyPreview);
